import { useMemo, useState } from 'react';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/** One decision in the queue: a new self-serve booking or a patient
 * reschedule request. Both get the identical Confirm / Decline pair. */
export interface RequestCardData {
  /** Unique across kinds (prefix by kind when mapping). */
  key: string;
  kind: 'booking' | 'reschedule';
  bookingProspectId?: string;
  appointmentId?: string | null;
  requestId?: string;
  name: string;
  providerName?: string | null;
  /** The slot the decision is about (requested time for reschedules). */
  startTime: string | null;
  durationMinutes?: number | null;
  serviceName?: string | null;
  createdAt?: string | null;
  email?: string | null;
  phone?: string | null;
  reason?: string | null;
  /** Unmet gate requirements worth flagging ("Paperwork pending"…). */
  blockers: string[];
  /** The appointment already confirmed itself (auto-lock) — accepting
   * resolves identity only. */
  autoConfirmed?: boolean;
  /** Reschedule: where the booking currently sits. */
  fromStartTime?: string | null;
  /** The booking re-entered review because the patient moved it. */
  rescheduledByPatient?: boolean;
}

/** A request resolved in this session — kept visible where its card was. */
export interface ResolvedRow {
  key: string;
  name: string;
  startTime: string | null;
  /** Set when acceptance resolved to a patient — links to their page. */
  patientId: string | null;
}

export interface BookingRequestsPanelProps {
  requests: RequestCardData[];
  resolved: ResolvedRow[];
  isLoading?: boolean;
  /** Decision failure to surface (e.g. a reschedule slot was just taken). */
  errorMessage?: string | null;
  /** Subject-language plural, e.g. "patients" / "clients". */
  subjectPlural: string;
  /** Card whose mutation is in flight (disables its buttons). */
  busyKey?: string | null;
  selectedKey?: string | null;
  onSelect?: (card: RequestCardData) => void;
  onConfirm: (card: RequestCardData) => void;
  onDecline: (card: RequestCardData) => void;
  onClose: () => void;
}

function formatTimeRange(
  startTime: string | null,
  durationMinutes?: number | null,
): string {
  if (!startTime) return 'Time unavailable';
  const start = new Date(startTime);
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  };
  const startLabel = start.toLocaleTimeString(undefined, timeOptions);
  if (!durationMinutes) return startLabel;
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  return `${startLabel}–${end.toLocaleTimeString(undefined, timeOptions)}`;
}

function formatShortStart(startTime?: string | null): string {
  if (!startTime) return 'time unavailable';
  return new Date(startTime).toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function dayKey(startTime: string | null): string {
  if (!startTime) return 'unscheduled';
  return new Date(startTime).toDateString();
}

function dayLabel(startTime: string | null): string {
  if (!startTime) return 'Time unavailable';
  const date = new Date(startTime);
  const monthDay = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return `Today · ${monthDay}`;
  if (date.toDateString() === tomorrow.toDateString())
    return `Tomorrow · ${monthDay}`;
  const weekday = date.toLocaleDateString(undefined, { weekday: 'long' });
  return `${weekday} · ${monthDay}`;
}

/** "booked 25 min ago" freshness signal. */
function timeAgo(createdAt?: string | null): string | null {
  if (!createdAt) return null;
  const elapsedMs = Date.now() - new Date(createdAt).getTime();
  if (elapsedMs < 0) return null;
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

type PanelEntry =
  | { type: 'request'; sortTime: number; card: RequestCardData }
  | { type: 'resolved'; sortTime: number; row: ResolvedRow };

function RequestCard({
  card,
  busy,
  selected,
  onSelect,
  onConfirm,
  onDecline,
}: {
  card: RequestCardData;
  busy: boolean;
  selected: boolean;
  onSelect?: () => void;
  onConfirm: () => void;
  onDecline: () => void;
}) {
  const age = timeAgo(card.createdAt);
  return (
    <Paper
      variant="outlined"
      onClick={onSelect}
      sx={{
        p: 1.5,
        cursor: onSelect ? 'pointer' : 'default',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.hover' : 'background.paper',
      }}
    >
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {formatTimeRange(card.startTime, card.durationMinutes)}
          {card.serviceName && (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {card.serviceName}
              {card.durationMinutes ? ` · ${card.durationMinutes} min` : ''}
            </Typography>
          )}
        </Typography>
        {age && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ flexShrink: 0 }}
          >
            {age}
          </Typography>
        )}
      </Stack>
      {card.providerName && (
        <Typography variant="caption" color="text.secondary">
          with {card.providerName}
        </Typography>
      )}

      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: 'center', mt: 0.25, flexWrap: 'wrap' }}
      >
        <Typography variant="body2">{card.name}</Typography>
        {card.kind === 'reschedule' && (
          <>
            <Chip size="small" variant="outlined" color="info" label="Reschedule" />
            <Typography variant="caption" color="text.secondary">
              from {formatShortStart(card.fromStartTime)}
            </Typography>
            <EastIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              this time
            </Typography>
          </>
        )}
        {card.rescheduledByPatient && (
          <Chip
            size="small"
            variant="outlined"
            color="info"
            label="Rescheduled by patient"
          />
        )}
        {card.autoConfirmed && (
          <Chip size="small" variant="outlined" label="Auto-confirmed" />
        )}
        {card.blockers.map(blocker => (
          <Chip
            key={blocker}
            size="small"
            variant="outlined"
            color="warning"
            label={blocker}
          />
        ))}
      </Stack>

      {(card.email || card.phone || card.reason) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.25 }}
        >
          {[card.email, card.phone].filter(Boolean).join(' · ')}
          {card.reason && (
            <>
              {(card.email || card.phone) && ' — '}
              <i>“{card.reason}”</i>
            </>
          )}
        </Typography>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1, alignItems: 'center' }}
        onClick={event => event.stopPropagation()}
      >
        <Button
          variant="contained"
          size="small"
          disabled={busy}
          startIcon={
            busy ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <CheckCircleOutlinedIcon />
            )
          }
          onClick={onConfirm}
        >
          Confirm
        </Button>
        <Button
          variant="text"
          size="small"
          color="inherit"
          disabled={busy}
          onClick={onDecline}
        >
          Decline
        </Button>
      </Stack>
    </Paper>
  );
}

/** The booking-requests queue (PRD D1, rebuilt per GDN-2436 alignment):
 * time-first cards grouped by appointment day, a provider filter for group
 * practices, identical Confirm/Decline on every card, and requests resolved
 * this session collapse to a linked confirmation row. Rendered non-modally
 * beside the calendar; selection drives the calendar's date. */
export function BookingRequestsPanel({
  requests,
  resolved,
  isLoading = false,
  errorMessage = null,
  subjectPlural,
  busyKey = null,
  selectedKey = null,
  onSelect,
  onConfirm,
  onDecline,
  onClose,
}: BookingRequestsPanelProps) {
  const [providerFilter, setProviderFilter] = useState<string | null>(null);

  const providers = useMemo(
    () =>
      [...new Set(requests.map(card => card.providerName).filter(Boolean))] as string[],
    [requests],
  );

  const groups = useMemo(() => {
    const visible = requests.filter(
      card => !providerFilter || card.providerName === providerFilter,
    );
    const entries: PanelEntry[] = [
      ...visible.map(card => ({
        type: 'request' as const,
        sortTime: card.startTime ? new Date(card.startTime).getTime() : Infinity,
        card,
      })),
      ...resolved.map(row => ({
        type: 'resolved' as const,
        sortTime: row.startTime ? new Date(row.startTime).getTime() : Infinity,
        row,
      })),
    ];
    const byDay = new Map<string, { label: string; entries: PanelEntry[] }>();
    for (const entry of entries.sort((a, b) => a.sortTime - b.sortTime)) {
      const startTime =
        entry.type === 'request' ? entry.card.startTime : entry.row.startTime;
      const key = dayKey(startTime);
      if (!byDay.has(key)) {
        byDay.set(key, { label: dayLabel(startTime), entries: [] });
      }
      byDay.get(key)!.entries.push(entry);
    }
    return [...byDay.values()];
  }, [requests, resolved, providerFilter]);

  const pendingCount = requests.length;

  return (
    <Paper
      variant="outlined"
      square
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderTop: 'none',
        borderBottom: 'none',
        borderRight: 'none',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Booking requests
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pendingCount === 0
              ? 'Nothing waiting on you'
              : `${pendingCount} waiting on your review`}
          </Typography>
        </div>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close booking requests"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      {providers.length > 1 && (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ px: 2, py: 1, flexWrap: 'wrap', rowGap: 0.75 }}
        >
          <Chip
            size="small"
            label={`All (${requests.length})`}
            color={providerFilter === null ? 'primary' : 'default'}
            variant={providerFilter === null ? 'filled' : 'outlined'}
            onClick={() => setProviderFilter(null)}
          />
          {providers.map(provider => (
            <Chip
              key={provider}
              size="small"
              label={`${provider} (${
                requests.filter(card => card.providerName === provider).length
              })`}
              color={providerFilter === provider ? 'primary' : 'default'}
              variant={providerFilter === provider ? 'filled' : 'outlined'}
              onClick={() => setProviderFilter(provider)}
            />
          ))}
        </Stack>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
        {errorMessage && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {errorMessage}
          </Alert>
        )}
        {isLoading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={110} />
            <Skeleton variant="rounded" height={110} />
          </Stack>
        ) : groups.length === 0 ? (
          <Stack
            spacing={1}
            sx={{ alignItems: 'center', py: 6, textAlign: 'center' }}
          >
            <InboxOutlinedIcon color="disabled" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              You&apos;re all caught up — new {subjectPlural} who book online
              will show up here.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {groups.map(group => (
              <div key={group.label}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  {group.label}
                </Typography>
                <Stack spacing={1}>
                  {group.entries.map(entry =>
                    entry.type === 'resolved' ? (
                      <Paper
                        key={entry.row.key}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          color: 'success.main',
                          borderColor: 'success.light',
                        }}
                      >
                        <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2">
                          {entry.row.patientId ? (
                            <Link
                              component={RouterLink}
                              to={`/patient/${entry.row.patientId}`}
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.row.name}
                            </Link>
                          ) : (
                            <Typography
                              component="span"
                              sx={{ fontWeight: 600 }}
                              variant="body2"
                            >
                              {entry.row.name}
                            </Typography>
                          )}{' '}
                          confirmed
                        </Typography>
                      </Paper>
                    ) : (
                      <RequestCard
                        key={entry.card.key}
                        card={entry.card}
                        busy={busyKey === entry.card.key}
                        selected={selectedKey === entry.card.key}
                        onSelect={
                          onSelect ? () => onSelect(entry.card) : undefined
                        }
                        onConfirm={() => onConfirm(entry.card)}
                        onDecline={() => onDecline(entry.card)}
                      />
                    ),
                  )}
                </Stack>
              </div>
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
